import type { LinkProps } from '@tanstack/react-router';
import type { VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';
import type { textVariants } from './variants';

export type TextElement = 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'label';
export type DateFormat = 'YYYY/MM/DD' | 'YYYY/MM/DD HH:mm:ss';
type ViewMode =
  | {
      viewMode?: never;
    }
  | {
      /** 金額でフォーマットする */
      viewMode: 'number';
      unit?: 'thousand' | 'yen' | undefined;
    }
  | {
      /** 日付でフォーマットする */
      viewMode: 'date';
      /** 日付フォーマット（デフォルト: 'YYYY/MM/DD HH:mm'） */
      format?: DateFormat;
    };

type TruncateMode =
  | {
      truncate?: never;
      enabledTooltip?: never;
    }
  | {
      /**
       * テキストを省略表示する
       * - true: 1行で省略（text-ellipsis）
       * - 数値: 指定した行数で省略（line-clamp）
       */
      truncate: boolean | number;
      /**
       * 省略時にツールチップを表示する（デフォルト: true）
       * truncateが設定されている場合のみ指定可能
       */
      enabledTooltip?: boolean;
    };

export type BaseTextProps = VariantProps<typeof textVariants> & {
  htmlFor?: string | undefined;
  children: ReactNode;
  id?: string;
  className?: string | undefined;
  onClick?: () => void;
  hint?: { value: string; className?: string; hidden?: boolean };
} & ViewMode &
  TruncateMode;

export type TextPropsWithAs = BaseTextProps & {
  as?: TextElement;
  to?: never;
  href?: never;
};

export type TextPropsWithLink = BaseTextProps & {
  as?: never;
  to: NonNullable<LinkProps['to']>;
  search?: LinkProps['search'];
  params?: LinkProps['params'];
  hash?: LinkProps['hash'];
  href?: never;
};

export type TextPropsWithHref = BaseTextProps & {
  as?: never;
  to?: never;
  href: string | undefined;
  /** 別タブで開く（デフォルト: false） */
  openInNewTab?: boolean;
};

export type TextProps = TextPropsWithAs | TextPropsWithLink | TextPropsWithHref;

export const variantElementMap: Record<
  NonNullable<VariantProps<typeof textVariants>['variant']>,
  TextElement
> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  body: 'p',
  label: 'label',
  subhead: 'p',
  link: 'p',
  description: 'p',
};
